import React, { useState, useEffect, useContext } from "react";

import { useQuery } from "@apollo/client";
import { DocumentNode } from "graphql";

import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import LoadingSpinner from "@/components/ui-kit/Loading/LoadingSpinner/LoadingSpinner";
import { TextElement } from "@/components/ui-kit/TextElement/TextElement";
import { ZENDESK_BUTTON, ZENDESK_TABLE } from "@/constants";
import SearchContext from "@/context/SearchContext";
import { useNavigation } from "@/hooks/useNavigation";
import { requestArticle } from "@/queries/search";
import { ContentfulProduct } from "@/types/common";
import { LinkedItem, ArticleQueryData } from "@/types/search";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";

import styles from "./ArticleSearch.module.scss";
import ComparisonTable from "./ComparisonTable";
import FaqDisclaimer from "./FaqDisclaimer";
import LinkedItems from "./LinkedItems";
import SearchResultsLayout from "./SearchResultsLayout";
import { getFormattedButton, getInteractiveElements, stripZendeskSpecialStrings } from "./utils";

export interface ArticleSearchProps {
  data: ContentfulProduct | LinkedItem | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (item: any, product: object) => void;
}

// ------------------- Component -------------------

const ArticleSearch: React.FC<ArticleSearchProps> = ({ data, onClick }) => {
  const { recommendClient, searchState, search } = useContext(SearchContext);
  const [relatedFAQsLoading, setRelatedFAQsLoading] = useState(true);
  const [relatedFAQs, setRelatedFAQs] = useState<LinkedItem[]>([]);
  const navigate = useNavigation();

  useEffect(() => {
    const fetchRelatedFAQs = async () => {
      try {
        const FAQs = await recommendClient.getRecommendations([
          {
            indexName: process.env.NEXT_PUBLIC_ALGOLIA_ZENDESK_INDEX_NAME ?? "Zendesk_production",
            objectID: data?.objectID ?? "",
            model: "related-products",
            maxRecommendations: 5,
          },
        ]);
        setRelatedFAQs(FAQs?.results[0]?.hits || []);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Algolia API error related articles ${error}`);
      } finally {
        setRelatedFAQsLoading(false);
      }
    };
    fetchRelatedFAQs();
  }, [recommendClient, data?.objectID]);

  const { loading, data: articleData } = useQuery<ArticleQueryData>(
    requestArticle as DocumentNode,
    {
      notifyOnNetworkStatusChange: true,
      variables: { id: data?.id },
      context: { clientName: "searchLink" },
    }
  );

  if (loading || relatedFAQsLoading) {
    return (
      <div className="relative w-full h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (!loading && !articleData?.ShowArticle?.article?.body) {
    return (
      <div className="text-center mx-auto">
        <TextElement
          className={cn("title--small-i", "mb-2", "md:mb-4")}
          text="Something went wrong"
        />
        <TextElement
          className={cn("body--large")}
          text="Sorry, something went wrong. Please try again later."
        />
      </div>
    );
  }

  const interactiveElements = getInteractiveElements(articleData?.ShowArticle?.article?.body) || [];

  return (
    <>
      <SearchResultsLayout>
        <div className={``}>
          <TextElement
            className={cn("title--small-i", "mb-2", "md:mb-8")}
            text={articleData?.ShowArticle?.article?.title || ""}
          />
          <div
            className={cn(styles.zdArticleBody, "zd-article-body")}
            dangerouslySetInnerHTML={{
              __html: stripZendeskSpecialStrings(articleData?.ShowArticle?.article?.body || ""),
            }}
          />
          {interactiveElements.map((element: string, index: number) => (
            <div key={index} className={`mt-9`}>
              {element === ZENDESK_TABLE && (
                <div>
                  <ComparisonTable />
                </div>
              )}

              {element.includes(ZENDESK_BUTTON) && (
                <ButtonNova
                  level="Secondary"
                  label={getFormattedButton(element)[0]}
                  icon="Right"
                  width="100%"
                  className="capitalize"
                  onClick={() => {
                    const [label, destination] = getFormattedButton(element);
                    GA4.onSearchClicked({
                      name: "Search",
                      search_term: search,
                      location: "navigation",
                      state: searchState,
                      text: label,
                      destination: `/${destination}/`,
                      type: "Link",
                    });
                    navigate(`/${destination}`);
                  }}
                />
              )}
            </div>
          ))}
          <FaqDisclaimer className="mt-20 hidden lg:block" />
        </div>
        <div>
          <LinkedItems title="Related" items={relatedFAQs} onClick={onClick} />
        </div>
      </SearchResultsLayout>
      <FaqDisclaimer className="md:mb-6 lg:hidden" />
    </>
  );
};

export default ArticleSearch;
